import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { BsExclamationTriangle } from "react-icons/bs";
import { activeCurrencies } from "utils";
import useAuthStore from "store/authStore";
import { Role } from "utils/server-constants/roles";
import StepPanel from "../StepPanel";

const PrepareStep = ({ onContinue }) => {
	const history = useHistory();
	const hasRoles = useAuthStore((state) => state.hasRoles);
	const showPublisherColumns = hasRoles([Role.SuperAdmin, Role.Admin]);

	const columns = useMemo(
		() => [
			{ header: "Title", type: "Text", required: true, example: "The Great Book" },
			{ header: "Author", type: "Text", required: true, example: "John Doe" },
			{
				header: `${activeCurrencies.USD.label} Price`,
				type: "Numeric",
				required: true,
				example: "19.99",
			},
			{
				header: `${activeCurrencies.ZWL.label} Price`,
				type: "Numeric",
				required: false,
				example: "1500",
			},
			{
				header: "ISBN Number",
				type: "Text",
				required: true,
				example: "978-1234567890",
			},
			...(showPublisherColumns
				? [
						{
							header: "Publisher Email",
							type: "Email",
							required: true,
							example: "publisher@example.com",
						},
						{
							header: "Publisher Firstname",
							type: "Text",
							required: true,
							example: "Jane",
						},
						{
							header: "Publisher Lastname",
							type: "Text",
							required: true,
							example: "Smith",
						},
						{
							header: "Publishing Company",
							type: "Text",
							required: true,
							example: "ABC Publishing",
						},
				  ]
				: []),
			{
				header: "Category",
				type: "Academic or Non-Academic",
				required: true,
				example: "Academic",
			},
			{
				header: "Collection",
				type: "Text",
				required: true,
				example: "Best Sellers",
			},
			{
				header: "Summary",
				type: "Text",
				required: false,
				example: "A captivating story...",
			},
		],
		[showPublisherColumns]
	);

	return (
		<StepPanel
			stepNumber={1}
			title="Prepare your spreadsheet"
			description="Your Excel or CSV file must use these exact column headers. Required fields cannot be empty or the import will fail."
			nav={{
				onBack: () => history.goBack(),
				onNext: onContinue,
				backLabel: "Cancel",
				nextLabel: "Continue to upload →",
			}}
		>
			<div className="space-y-6">
				<div className="overflow-x-auto rounded-xl border border-gray-200">
					<table className="min-w-full divide-y divide-gray-200 text-left text-sm">
						<thead className="bg-gray-50">
							<tr>
								<th
									scope="col"
									className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500"
								>
									Column
								</th>
								<th
									scope="col"
									className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500"
								>
									Type
								</th>
								<th
									scope="col"
									className="hidden px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 sm:table-cell"
								>
									Example
								</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-gray-100 bg-white">
							{columns.map((field) => (
								<tr key={field.header}>
									<td className="px-4 py-3">
										<div className="flex flex-wrap items-center gap-2">
											<span className="font-medium text-gray-900">
												{field.header}
											</span>
											{field.required ? (
												<span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-red-700">
													Required
												</span>
											) : (
												<span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
													Optional
												</span>
											)}
										</div>
										<p className="mt-1 font-mono text-xs text-gray-500 sm:hidden">
											{field.example}
										</p>
									</td>
									<td className="px-4 py-3 text-gray-600">{field.type}</td>
									<td className="hidden px-4 py-3 font-mono text-xs text-gray-500 sm:table-cell">
										{field.example}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>

				<div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5">
					<h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-900">
						<BsExclamationTriangle className="h-4 w-4 text-amber-600" aria-hidden />
						Important notes
					</h3>
					<ul className="space-y-1.5 text-sm text-amber-800">
						{showPublisherColumns ? (
							<li>
								<strong>Publisher Email</strong> should match the Senior
								Publisher&apos;s account email.
							</li>
						) : null}
						<li>
							<strong>Required fields</strong> cannot be empty or that row
							will fail.
						</li>
						<li>
							<strong>Category</strong> must be exactly{" "}
							<code className="rounded bg-white px-1 py-0.5 text-xs">
								Academic
							</code>{" "}
							or{" "}
							<code className="rounded bg-white px-1 py-0.5 text-xs">
								Non-Academic
							</code>
							. Values like &quot;Fiction&quot; are rejected.
						</li>
						<li>
							<strong>ISBN Number</strong> is required for every row.
						</li>
						<li>
							<strong>Price values</strong> should be numeric (e.g. 19.99,
							1500).
						</li>
						<li>
							<strong>Email format</strong> must be valid email addresses.
						</li>
						<li>
							Covers and PDFs are attached separately via{" "}
							<strong>Bulk upload books</strong>.
						</li>
					</ul>
				</div>
			</div>
		</StepPanel>
	);
};

export default PrepareStep;
